import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Eye, Lock } from "@phosphor-icons/react";
import { usePortfolio } from "@/data/portfolio";
import PdfViewer from "@/components/PdfViewer";

// Home-page portfolio slider — shows curated sample drawings 3-per-slide
// (1 on mobile). Clicking a card opens the protected PDF viewer modal.
export default function PortfolioSlider() {
  const { items, loaded } = usePortfolio();
  const [active, setActive] = useState(null);
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(3);
  const trackRef = useRef(null);

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w < 640) setPerView(1);
      else if (w < 1024) setPerView(2);
      else setPerView(3);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  const featured = useMemo(() => items.filter((it) => it.show_on_home !== false), [items]);
  const maxIndex = Math.max(0, featured.length - perView);
  const safeIndex = Math.min(index, maxIndex);

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(maxIndex, i + 1));

  if (loaded && featured.length === 0) return null;

  return (
    <section
      className="bg-white py-24 md:py-32 border-y border-zinc-200"
      data-testid="home-portfolio-slider"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 mb-12 items-end">
          <div className="md:col-span-7">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
              Portfolio · Recent samples
            </div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-zinc-950 leading-[1.05]">
              See exactly what lands in your inbox.
            </h2>
            <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-2xl">
              A redacted look at drawings we&apos;ve delivered across the U.S. View-only —
              <span className="inline-flex items-center gap-1 ml-1">
                <Lock size={13} weight="bold" className="text-[#0044FF]" /> downloads disabled
              </span>.
            </p>
          </div>
          <div className="md:col-span-5 flex md:justify-end gap-2">
            <button
              type="button"
              onClick={prev}
              disabled={safeIndex === 0}
              className="w-12 h-12 border border-zinc-900 flex items-center justify-center text-zinc-900 hover:bg-zinc-900 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous samples"
              data-testid="portfolio-slider-prev"
            >
              <ArrowLeft size={16} weight="bold" />
            </button>
            <button
              type="button"
              onClick={next}
              disabled={safeIndex >= maxIndex}
              className="w-12 h-12 border border-zinc-900 flex items-center justify-center text-zinc-900 hover:bg-zinc-900 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Next samples"
              data-testid="portfolio-slider-next"
            >
              <ArrowRight size={16} weight="bold" />
            </button>
            <Link
              to="/portfolio"
              className="ml-3 px-5 h-12 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-medium btn-lift"
              data-testid="portfolio-slider-view-all"
            >
              View full portfolio <ArrowRight size={13} weight="bold" />
            </Link>
          </div>
        </div>

        <div className="relative overflow-hidden border border-zinc-200">
          <div
            ref={trackRef}
            className="flex transition-transform duration-500 ease-out"
            style={{
              transform: `translateX(-${(safeIndex * 100) / perView}%)`,
            }}
          >
            {featured.map((it, i) => (
              <div
                key={it.slug}
                className="flex-shrink-0 px-px"
                style={{ width: `${100 / perView}%` }}
              >
                <button
                  type="button"
                  onClick={() => setActive(it)}
                  className="group block w-full text-left bg-white hover:bg-zinc-50 transition-colors"
                  data-testid={`portfolio-slide-${it.slug}`}
                >
                  <div
                    className="aspect-[4/3] bg-zinc-100 bg-contain bg-no-repeat bg-center relative overflow-hidden"
                    style={it.thumb_url ? { backgroundImage: `url(${it.thumb_url})` } : {}}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/70 via-zinc-950/10 to-transparent" />
                    <div className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-[0.25em] text-white/90 bg-zinc-950/50 px-2 py-1 backdrop-blur-sm">
                      FIG {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity bg-zinc-950/40">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Eye size={16} weight="duotone" /> View sample
                      </div>
                    </div>
                  </div>
                  <div className="p-5 md:p-6">
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">
                      {it.service_title}
                    </div>
                    <div className="mt-2 font-display text-xl md:text-2xl text-zinc-950 tracking-tight leading-tight">
                      {it.title}
                    </div>
                    <div className="mt-3 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500">
                      {it.state || "USA"}{it.year ? ` · ${it.year}` : ""}
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* slide indicator dots */}
        {maxIndex > 0 && (
          <div className="mt-6 flex items-center justify-center gap-2" data-testid="portfolio-slider-dots">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`portfolio-slider-dot-${i}`}
                className={`h-1.5 transition-all ${
                  i === safeIndex ? "w-8 bg-zinc-950" : "w-3 bg-zinc-300 hover:bg-zinc-500"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {active && <PdfViewer item={active} onClose={() => setActive(null)} />}
    </section>
  );
}
